import React, { useState } from 'react';
import { apiService } from '../../services/api';

const DocumentUpload = ({ value, onChange, placeholder, className = '' }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [uniqueId] = useState(() => Math.random().toString(36).substr(2, 9));

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!validTypes.includes(file.type)) {
      setError('Please select a valid document file (PDF, DOC, or DOCX)');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }

    setError('');
    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);

      // Use XMLHttpRequest for progress tracking
      const xhr = new XMLHttpRequest();
      const token = localStorage.getItem('token');
      
      const uploadPromise = new Promise((resolve, reject) => {
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const progress = Math.round((e.loaded * 100) / e.total);
            setUploadProgress(progress);
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status === 200) {
            try {
              const response = JSON.parse(xhr.responseText);
              resolve(response);
            } catch (error) {
              reject(new Error('Invalid response'));
            }
          } else {
            reject(new Error(`Upload failed: ${xhr.statusText}`));
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Network error'));
        });

        const apiUrl = process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? 'http://localhost:8000/api' : '/api');
      xhr.open('POST', `${apiUrl}/uploads/single/documents`);
        xhr.withCredentials = true;
        if (token) {
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        }
        xhr.send(formData);
      });

      const response = await uploadPromise;

      if (response.data && response.data.file && response.data.file.url) {
        // Convert relative URL to full URL
        const baseUrl = `${window.location.protocol}//${window.location.host}`;
        const fullUrl = response.data.file.url.startsWith('http') 
          ? response.data.file.url 
          : `${baseUrl}${response.data.file.url}`;
        onChange(fullUrl);
      }
    } catch (error) {
      console.error('Upload error:', error);
      setError('Failed to upload document. Please try again.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleClear = () => {
    onChange('');
    setError('');
  };

  const getFileIcon = (url) => {
    if (!url) return 'fas fa-file';
    if (url.toLowerCase().includes('.pdf')) return 'fas fa-file-pdf';
    if (url.toLowerCase().includes('.doc')) return 'fas fa-file-word';
    return 'fas fa-file';
  };

  const getFileName = (url) => {
    if (!url) return '';
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      return pathname.split('/').pop() || 'document';
    } catch {
      return url.split('/').pop() || 'document';
    }
  };

  return (
    <div className={className}>
      <div className="flex gap-2">
        <input
          type="url"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 px-3 py-2 border border-border_gray rounded text-sm focus:outline-none focus:ring-2 focus:ring-crimson"
          placeholder={placeholder || 'Document URL'}
          disabled={uploading}
        />
        
        <div className="relative">
          <input
            type="file"
            onChange={handleFileSelect}
            className="hidden"
            id={`upload-doc-${uniqueId}`}
            accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            disabled={uploading}
          />
          <label
            htmlFor={`upload-doc-${uniqueId}`}
            className={`px-3 py-2 bg-gray-100 text-slate rounded text-sm hover:bg-gray-200 cursor-pointer flex items-center gap-2 ${
              uploading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {uploading ? (
              <>
                <div className="w-3 h-3 border-2 border-slate border-t-transparent rounded-full animate-spin"></div>
                <span>{uploadProgress}%</span>
              </>
            ) : (
              <>
                <i className="fas fa-upload"></i>
                <span>Upload</span>
              </>
            )}
          </label>
        </div>

        {value && (
          <button
            type="button"
            onClick={handleClear}
            className="px-3 py-2 text-red-500 hover:bg-red-50 rounded text-sm"
            title="Clear document"
          >
            <i className="fas fa-times"></i>
          </button>
        )}
      </div>

      {error && (
        <p className="text-red-500 text-sm mt-1">
          <i className="fas fa-exclamation-circle mr-1"></i>
          {error}
        </p>
      )}

      {value && value !== '#' && (
        <div className="mt-2 flex items-center gap-2 p-2 bg-gray-50 border border-gray-200 rounded">
          <i className={`${getFileIcon(value)} text-crimson`}></i>
          <span className="text-sm text-gray-700 flex-1">{getFileName(value)}</span>
          {value.startsWith('http') && (
            <a
              href={value}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 text-sm"
              title="View document"
            >
              <i className="fas fa-external-link-alt"></i>
            </a>
          )}
        </div>
      )}
    </div>
  );
};

export default DocumentUpload;